import { Component, Vue, Watch } from 'nuxt-property-decorator';
import { guid } from '@/components/utils/core/uid';
import SearchForQuery from '~/components/common/UniversalSearch/apollo/SearchFor.query';
import type { UniversalSearchResult } from '@/components/common/UniversalSearch/types';

const MIN_CHARS = 2;
const DEBOUNCE_INTERVAL = 200;
const PER_PAGE = 10;

@Component({
  // @ts-ignore
  apollo: {
    search: {
      query: SearchForQuery,
      variables() {
        return {
          query: this.searchTerm.trim(),
        };
      },
      error(e) {
        this.$notice.error(e);
        this.isCalled = true;
      },
      update(data: { search: UniversalSearchResult[] }) {
        this.results = data.search;
        this.isCalled = true;
        this.hasMore = this.results.length >= PER_PAGE;
        this.logSearchEnded();
      },
      skip() {
        return this.searchTerm.trim().length < MIN_CHARS;
      },
      watchLoading(isLoading) {
        if (isLoading) {
          this.logSearchStarted();
        }
      },
      debounce: DEBOUNCE_INTERVAL,
    },
  },
})
export default class UniversalSearchMixin extends Vue {
  protected queryId = guid();
  protected searchTerm = '';
  protected results: UniversalSearchResult[] = [];
  protected suggestions = ['Top companies', 'Account Executive salaries', 'Head of Sales people', 'Layoffs posts'];
  protected page: number = 1;
  protected isLoadingMore: boolean = false;
  protected hasMore: boolean = true;
  private isCalled: boolean = false;

  get query() {
    return this.searchTerm.trim();
  }

  get isSearching() {
    return this.$apollo.queries.search.loading && !this.isLoadingMore;
  }

  get isResultsVisible() {
    if (this.query.length < MIN_CHARS) return false;
    return this.isCalled && this.results.length > 0;
  }

  get isInitialStateVisible() {
    if (this.query.length < MIN_CHARS) return true;
    if (this.isResultsVisible) return false;
    return !this.isSearching;
  }

  get isNothingFound() {
    if (this.isSearching) return false;
    if (this.isResultsVisible) return false;
    return this.isCalled && this.query.length >= MIN_CHARS;
  }

  async loadMore() {
    try {
      const nextPage = this.page + 1;
      this.isLoadingMore = true;

      const { data } = await this.$apollo.queries.search.fetchMore({
        variables: {
          query: this.searchTerm.trim(),
          page: nextPage,
          perPage: PER_PAGE,
        },
        updateQuery: (
          data: { search: UniversalSearchResult[] },
          { fetchMoreResult }: { fetchMoreResult?: { search: UniversalSearchResult[] } },
        ) => {
          return {
            ...data,
            search: [...data.search, ...(fetchMoreResult?.search ?? [])],
          };
        },
      });

      if (!data?.search?.length) {
        // if no items present, do not query for that again
        this.hasMore = false;
      } else {
        this.page = nextPage;
      }
    } finally {
      this.isLoadingMore = false;
    }
  }

  @Watch('query')
  handleQueryChange() {
    // reset the search state
    this.page = 1;
    this.hasMore = true;
    this.isCalled = false;
  }

  logSearchStarted() {
    this.queryId = guid();
    this.$analytics.logEvent('search__query__type', { query: this.query, queryId: this.queryId, version: 'v1' });
  }

  logSearchEnded() {
    const counters = this.results.reduce<Record<string, number>>((acct, result) => {
      const key = result.category;
      return {
        ...acct,
        [key]: (acct[key] ?? 0) + 1,
      };
    }, {});
    this.$analytics.logEvent('search__results__loaded', {
      query: this.query,
      queryId: this.queryId,
      version: 'v1',
      ...counters,
    });
  }
}
